import { EnhancedPattern, PatternCategory, PatternClassification } from '@/types/trading-analysis-types';

export class PatternClassifier {
  /**
   * Classify a pattern based on success rate and statistical significance
   */
  static classifyPattern(pattern: EnhancedPattern): PatternClassification {
    const { successRate, frequency, nextWinCount, nextLossCount } = pattern;
    const totalSamples = nextWinCount + nextLossCount;
    
    // Determine category based on success rate
    let category: PatternCategory;
    if (successRate > 75) {
      category = 'HIGH_WIN';
    } else if (successRate > 55) {
      category = 'MODERATE_WIN';
    } else if (successRate >= 45) {
      category = 'BALANCED';
    } else if (successRate >= 25) {
      category = 'MODERATE_LOSS';
    } else {
      category = 'HIGH_LOSS';
    }
    
    // Determine risk level based on frequency and sample size
    const risk = this.calculateRiskLevel(frequency, totalSamples, successRate);
    
    // Determine action recommendation
    const actionRecommendation = this.getActionRecommendation(category, risk, frequency);
    
    // Generate risk description
    const riskDescription = this.getRiskDescription(category, risk, frequency, successRate);
    
    return {
      category,
      risk,
      actionRecommendation,
      riskDescription
    };
  }
  
  /**
   * Calculate risk level based on pattern reliability
   */
  private static calculateRiskLevel(
    frequency: number, 
    totalSamples: number, 
    successRate: number
  ): 'very_low' | 'low' | 'medium' | 'high' | 'very_high' {
    // High frequency + adequate samples = lower risk
    if (frequency >= 10 && totalSamples >= 20) {
      return successRate > 80 || successRate < 20 ? 'medium' : 'very_low';
    }
    
    if (frequency >= 5 && totalSamples >= 10) {
      return successRate > 85 || successRate < 15 ? 'high' : 'low';
    }
    
    if (frequency >= 3 && totalSamples >= 6) {
      return 'medium';
    }
    
    return 'high';
  }
  
  /**
   * Get trading action recommendation
   */
  private static getActionRecommendation(
    category: PatternCategory, 
    risk: 'very_low' | 'low' | 'medium' | 'high' | 'very_high',
    frequency: number
  ): 'strong_buy' | 'buy' | 'neutral' | 'avoid' | 'reverse' {
    // High risk patterns should be avoided regardless of category
    if (risk === 'very_high' || risk === 'high') {
      return 'neutral';
    }
    
    // Low frequency patterns are less reliable
    if (frequency < 3) {
      return 'neutral';
    }
    
    switch (category) {
      case 'HIGH_WIN':
        return risk === 'very_low' ? 'strong_buy' : 'buy';
      case 'MODERATE_WIN':
        return 'buy';
      case 'BALANCED':
        return 'neutral';
      case 'MODERATE_LOSS':
        return 'avoid';
      case 'HIGH_LOSS':
        return frequency >= 5 ? 'reverse' : 'avoid';
      default:
        return 'neutral';
    }
  }
  
  /**
   * Generate human-readable risk description
   */
  private static getRiskDescription(
    category: PatternCategory,
    risk: 'very_low' | 'low' | 'medium' | 'high' | 'very_high',
    frequency: number,
    successRate: number
  ): string {
    const categoryDesc = {
      'HIGH_WIN': 'Highly favorable',
      'MODERATE_WIN': 'Moderately favorable', 
      'BALANCED': 'Neutral outcome',
      'MODERATE_LOSS': 'Moderately unfavorable',
      'HIGH_LOSS': 'Highly unfavorable'
    };
    
    const riskDesc = {
      'very_low': 'Very reliable',
      'low': 'Reliable',
      'medium': 'Moderately reliable', 
      'high': 'Low reliability',
      'very_high': 'Very low reliability'
    };
    
    const reliabilityNote = frequency >= 5 ? 
      `(${frequency} occurrences)` : 
      `(Only ${frequency} occurrences - limited data)`;
    
    return `${categoryDesc[category]} pattern with ${successRate.toFixed(1)}% win rate. ${riskDesc[risk]} ${reliabilityNote}`;
  }
  
  /**
   * Get patterns by category
   */
  static getPatternsByCategory(patterns: EnhancedPattern[]): Record<PatternCategory, EnhancedPattern[]> {
    const categorized: Record<PatternCategory, EnhancedPattern[]> = {
      HIGH_WIN: [],
      MODERATE_WIN: [],
      BALANCED: [],
      MODERATE_LOSS: [],
      HIGH_LOSS: []
    };
    
    patterns.forEach(pattern => {
      if (pattern.classification) {
        categorized[pattern.classification.category].push(pattern);
      }
    });
    
    return categorized;
  }
  
  /**
   * Get high-risk patterns (potential losses)
   */
  static getHighRiskPatterns(patterns: EnhancedPattern[]): EnhancedPattern[] {
    return patterns.filter(p => 
      p.classification && 
      (p.classification.category === 'HIGH_LOSS' || p.classification.category === 'MODERATE_LOSS') &&
      p.frequency >= 3 // Ensure adequate sample size
    );
  }
  
  /**
   * Get opportunity patterns (potential wins)
   */
  static getOpportunityPatterns(patterns: EnhancedPattern[]): EnhancedPattern[] {
    return patterns.filter(p => 
      p.classification && 
      (p.classification.category === 'HIGH_WIN' || p.classification.category === 'MODERATE_WIN') &&
      p.frequency >= 3 // Ensure adequate sample size
    );
  }
  
  /**
   * Calculate pattern distribution
   */
  static calculateDistribution(patterns: EnhancedPattern[]): Record<PatternCategory, number> {
    const distribution: Record<PatternCategory, number> = {
      HIGH_WIN: 0,
      MODERATE_WIN: 0,
      BALANCED: 0,
      MODERATE_LOSS: 0,
      HIGH_LOSS: 0
    };
    
    patterns.forEach(pattern => {
      if (pattern.classification) {
        distribution[pattern.classification.category]++;
      }
    });
    
    return distribution;
  }
}